import i18n from '../i18n';

export const formatNumber = (num) => {
  if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
  return num?.toString() || '0';
};

export const getRatingClass = (rating) => {
  if (rating >= 1.3) return 'rating-excellent';
  if (rating >= 1.1) return 'rating-good';
  if (rating >= 0.9) return 'rating-average';
  if (rating >= 0.7) return 'rating-below';
  return 'rating-poor';
};

/**
 * Get the base path under which static images are served.
 *
 * Images live under the app's asset mount (PUBLIC_URL, e.g. '/stats'),
 * independent of the current route. The SPA itself is served at root, but
 * assets/images stay under /stats so they don't collide with Django's
 * '/static' and the root namespace.
 * @returns {string} Image base path (e.g. '/stats'), or '' when unset.
 */
export const getImageBasePath = () => {
  return process.env.PUBLIC_URL || '';
};

/**
 * Get the full URL for a map image (card image or radar).
 * @param {string} imageUrl - Raw image URL from API.
 * @returns {string|null} Resolved URL under /images/maps/.
 */
export const getMapImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  if (imageUrl.startsWith('http')) return imageUrl;
  // Normalize: strip /static/maps/ or /maps/ prefix, rebuild under /images/maps/
  const cleaned = imageUrl.replace(/^\/static\/maps\//, '').replace(/^\/maps\//, '');
  // ?v=2: cache-buster. Missing files were once served as index.html with 7d
  // immutable cache headers; a new URL sidesteps those poisoned browser cache
  // entries. Bump the version if it ever happens again.
  return `${getImageBasePath()}/images/maps/${cleaned}?v=2`;
};

/**
 * Get the full URL for a medal image.
 * @param {string} imageUrl - Medal image_url from API (e.g. /static/medals/tourn_xxx_1.png).
 * @returns {string|null} Resolved URL under /images/medals/.
 */
export const getMedalImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  if (imageUrl.startsWith('http')) return imageUrl;
  const cleaned = imageUrl.replace(/^\/static\/medals\//, '').replace(/^\/images\/medals\//, '');
  return `${getImageBasePath()}/images/medals/${cleaned}`;
};

/**
 * Get the full URL for a shop flair emblem.
 *
 * The API sends a bare filename stem (the player's flair_emblem, e.g.
 * 'vargskallen') rather than a path — the emblem is a catalogue entry, not an
 * uploaded file, so the location belongs to the frontend. Images are served by
 * nginx from the achievement_images bind mount, the same chain as medals.
 *
 * @param {string} emblem - flair_emblem value from the API (filename stem).
 * @returns {string|null} Resolved URL under /images/achievements/emblems/, or
 *   null when the player has no emblem equipped.
 */
export const getEmblemImageUrl = (emblem) => {
  if (!emblem) return null;
  return `${getImageBasePath()}/images/achievements/emblems/${emblem}.png`;
};

/**
 * Get the full URL for a league team logo.
 *
 * Uploaded logos are stored relative to the medals dir (e.g.
 * 'team-logos/lteam_x.png') and served under /images/medals/. An external URL
 * (http...) is returned unchanged so a pasted logo link also works.
 *
 * @param {string} logoUrl - Team logo_url from the API.
 * @returns {string|null} Resolved image URL, or null when unset.
 */
export const getTeamLogoUrl = (logoUrl) => {
  if (!logoUrl) return null;
  if (logoUrl.startsWith('http')) return logoUrl;
  const cleaned = logoUrl.replace(/^\/+/, '').replace(/^images\/medals\//, '');
  return `${getImageBasePath()}/images/medals/${cleaned}`;
};

/**
 * Get the full URL for a news post's cover image.
 *
 * Uploaded covers are stored relative to the medals dir (e.g. 'news/news_x.png')
 * and served under /images/medals/. An external URL (http...) is returned
 * unchanged so a pasted image link also works.
 *
 * @param {string} imageUrl - News cover_image_url from the API.
 * @returns {string|null} Resolved image URL, or null when unset.
 */
export const getNewsImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  if (imageUrl.startsWith('http')) return imageUrl;
  const cleaned = imageUrl.replace(/^\/+/, '').replace(/^images\/medals\//, '');
  return `${getImageBasePath()}/images/medals/${cleaned}`;
};

/** Max team-emblem upload size in bytes — mirrors the API's 5 MB cap. */
export const TEAM_LOGO_MAX_BYTES = 5 * 1024 * 1024;

/** Accepted team-emblem MIME types — mirrors the API's allowed extensions. */
export const TEAM_LOGO_ACCEPT = 'image/png,image/jpeg,image/webp,image/gif';

/**
 * Validate a chosen team-emblem file against the API's size/type limits so the
 * user gets instant feedback instead of a silently-rejected upload.
 *
 * @param {File} file - The file selected in a file input.
 * @returns {string|null} A localized error message, or null when the file is OK.
 */
export const validateTeamLogo = (file) => {
  if (!file) return null;
  if (file.size > TEAM_LOGO_MAX_BYTES) return i18n.t('formatters.logoTooLarge');
  if (file.type && !TEAM_LOGO_ACCEPT.split(',').includes(file.type)) {
    return i18n.t('formatters.logoBadType');
  }
  return null;
};

/**
 * Map CS2 internal weapon names to display names.
 */
const WEAPON_DISPLAY_NAMES = {
  ak47: 'AK-47',
  awp: 'AWP',
  m4a1_silencer: 'M4A1-S',
  m4a1: 'M4A4',
  usp_silencer: 'USP-S',
  deagle: 'Desert Eagle',
  glock: 'Glock-18',
  p250: 'P250',
  famas: 'FAMAS',
  galil: 'Galil AR',
  aug: 'AUG',
  sg556: 'SG 553',
  ssg08: 'SSG 08',
  mac10: 'MAC-10',
  mp9: 'MP9',
  ump45: 'UMP-45',
  p90: 'P90',
  mp7: 'MP7',
  mp5sd: 'MP5-SD',
  nova: 'Nova',
  xm1014: 'XM1014',
  mag7: 'MAG-7',
  sawedoff: 'Sawed-Off',
  negev: 'Negev',
  m249: 'M249',
  knife: 'Knife',
  knife_t: 'Knife',
  taser: 'Zeus x27',
  hkp2000: 'P2000',
  elite: 'Dual Berettas',
  fiveseven: 'Five-SeveN',
  tec9: 'Tec-9',
  cz75a: 'CZ75-Auto',
  revolver: 'R8 Revolver',
  bizon: 'PP-Bizon',
  g3sg1: 'G3SG1',
  scar20: 'SCAR-20',
  hegrenade: 'HE Grenade',
  molotov: 'Molotov',
  incgrenade: 'Incendiary',
  inferno: 'Molotov',
  smokegrenade: 'Smoke',
  flashbang: 'Flashbang',
  decoy: 'Decoy',
  // Skadekällor som demon räknar som "vapen" men som inte är det
  planted_c4: 'C4 (bomb)',
  world: 'World',
  worldent: 'World',
  trigger_hurt: 'Map damage',
};

/**
 * Damage sources in the weapon stats that are not weapons and have no icon
 * (bomb explosion, fall damage, map hurt zones). Requesting their PNG only
 * produced 404s.
 */
const ICONLESS_SOURCES = new Set(['planted_c4', 'world', 'worldent', 'trigger_hurt']);

/**
 * Convert CS2 internal weapon name to display-friendly name.
 * @param {string} name - Internal weapon name (e.g. 'ak47', 'usp_silencer').
 * @returns {string} Display name.
 */
export const formatWeaponName = (name) => {
  if (!name) return '';
  return WEAPON_DISPLAY_NAMES[name] || name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

/**
 * Get the URL for a weapon image (official Steam CDN renders, PNG).
 *
 * Returns an empty string when there is no icon to show — no name, or a
 * non-weapon damage source (see ICONLESS_SOURCES). Callers render no
 * `<img>` for an empty URL.
 * @param {string} weaponName - Internal weapon name (e.g. 'ak47').
 * @returns {string} URL path to the weapon image, or '' when there is none.
 */
export const getWeaponIconUrl = (weaponName) => {
  if (!weaponName || ICONLESS_SOURCES.has(weaponName)) return '';
  const base = getImageBasePath();
  return `${base}/images/weapons/${weaponName}.png`;
};
